import { isBlocket } from '@borealis/aurora';
import { i18n } from '@lingui/core';
import { getHuckConfig } from '../utils/getHuckConfig.js';

export const lookupTitle = (model: string) => {
  switch (model) {
    case 'car-used':
    case 'car-used-business':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.car.title',
        comment: 'page title when looking up cars to sell',
        message: 'Fetch car details',
      });
    case 'car-leasing':
    case 'car-leasing-business':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.car-leasing.title',
        comment: 'page title when looking up cars that will be leased out',
        message: 'Fetch car details',
      });
    case 'car-new':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.car-new.title',
        comment: 'page title when looking up new cars to sell',
        message: 'Fetch car details',
      });
    case 'motorcycle':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.motorcycle.title',
        comment: 'page title when looking up motorcycles to sell',
        message: 'Fetch motorcycle details',
      });
    case 'moped':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.moped.title',
        comment: 'page title when looking up mopeds to sell',
        message: 'Fetch moped details',
      });
    case 'atv':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.atv.title',
        comment: 'page title when looking up atvs to sell',
        message: 'Fetch ATV details',
      });
    case 'snowmobile':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.snowmobile.title',
        comment: 'page title when looking up snowmobiles to sell',
        message: 'Fetch snowmobile details',
      });
    case 'a-tractor':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.a-tractor.title',
        comment: 'page title when looking up a-tractors to sell',
        message: 'Fetch A-Tractor details',
      });
    default:
      return '';
  }
};

export const lookupRegNoPlaceholder = (model: string) => {
  const config = getHuckConfig();
  const isSwedish = isBlocket(config.brand);

  switch (model) {
    case 'car-new':
    case 'car-used':
    case 'car-used-business':
    case 'car-leasing':
    case 'car-leasing-business':
      return isSwedish ? 'ABC123' : 'AB12345';
    case 'motorcycle':
      return isSwedish ? 'ABC123' : 'AB1234';
    case 'moped':
    case 'atv':
    case 'snowmobile':
      return 'AB1234';
    case 'a-tractor':
      return 'ABC123';
    default:
      return '';
  }
};

export const lookupCategoryMismatchTitle = (model: string) => {
  switch (model) {
    case 'car-used':
    case 'car-used-business':
    case 'car-new':
    case 'car-leasing':
    case 'car-leasing-business':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.car.title',
        comment: 'Shown when vehicle is found but in wrong category - car',
        message: 'It seems to be a car',
      });
    case 'motorcycle':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.motorcycle.title',
        comment: 'Shown when vehicle is found but in wrong category - motorcycle',
        message: 'It seems to be a motorcycle',
      });
    case 'moped':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.moped.title',
        comment: 'Shown when vehicle is found but in wrong category - moped',
        message: 'It seems to be a moped',
      });
    case 'atv':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.atv.title',
        comment: 'Shown when vehicle is found but in wrong category - ATV',
        message: 'It seems to be an ATV',
      });
    case 'snowmobile':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.snowmobile.title',
        comment: 'Shown when vehicle is found but in wrong category - snowmobile',
        message: 'It seems to be a snowmobile',
      });
    case 'a-tractor':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.a-tractor.title',
        comment: 'Shown when vehicle is found but in wrong category - A-tractor',
        message: 'It seems to be an A-tractor',
      });
    default:
      return '';
  }
};

export const lookupCategoryMismatchMessage = () => {
  return i18n.t({
    id: 'mobility-item-insertion.lookup.category-mismatch.message',
    comment: 'Explanation that vehicle was found in another category',
    message: 'Change category to create the ad. Note that the price may differ between categories.',
  });
};

export const lookupCategoryMismatchButton = (model: string) => {
  switch (model) {
    case 'car-used':
    case 'car-used-business':
    case 'car-new':
    case 'car-leasing':
    case 'car-leasing-business':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.car-used.button',
        comment: 'Button to redirect to correct vehicle category - car used',
        message: 'Create an ad for used car',
      });
    case 'motorcycle':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.motorcycle.button',
        comment: 'Button to redirect to correct vehicle category - motorcycle',
        message: 'Create an ad for motorcycle',
      });
    case 'moped':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.moped.button',
        comment: 'Button to redirect to correct vehicle category - moped',
        message: 'Create an ad for moped',
      });
    case 'atv':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.atv.button',
        comment: 'Button to redirect to correct vehicle category - ATV',
        message: 'Create an ad for ATV',
      });
    case 'snowmobile':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.snowmobile.button',
        comment: 'Button to redirect to correct vehicle category - snowmobile',
        message: 'Create an ad for snowmobile',
      });
    case 'a-tractor':
      return i18n.t({
        id: 'mobility-item-insertion.lookup.category-mismatch.a-tractor.button',
        comment: 'Button to redirect to correct vehicle category - A-tractor',
        message: 'Create an ad for A-tractor',
      });
    default:
      return '';
  }
};
